<template>
	<div
		class="graph-card"
		:class="[type, `type-${graphType}`]"
		:style="{
			'--reachedBackgroundColor': reachedBackgroundColor,
			'--reachedColor': reachedColor,
			'--reachedHoverBackgroundColor': reachedHoverBackgroundColor,
			'--notReachedBackgroundColor': notReachedBackgroundColor,
			'--notReachedColor': notReachedColor,
			'--aboveBackgroundColor': aboveBackgroundColor,
			'--aboveColor': aboveColor,
			'--noGoalBackgroundColor': noGoalBackgroundColor,
			'--noGoalColor': noGoalColor,
		}"
	>
		<div class="graph-header">
			<h3>{{ title }}</h3>
		</div>
		<div class="graph-main">
			<div class="graph-body">
				<div class="distribution">
					<div
						class="indicator-container"
						:class="{
							noGoal: goal === null,
						}"
						:style="{
							'--containerWidth': GetIndicatorContainerWidth(),
						}"
					>
						<div
							class="indicator-wrapper"
							@click="ChangeGoalQuestion(type)"
						>
							<span class="goal-value">{{ goal }}</span>
							<i class="fas fa-caret-right"></i>
						</div>
					</div>
					<div class="distribution-container">
						<div
							v-for="(item, index) in distributionData"
							:key="index"
							class="distribution-item"
							:class="{
								above: item.value > goal,
								reached: item.value === goal,
								reverse: reverse,
								noGoal: goal === null,
							}"
						>
							<div class="column-value-container">
								<div
									class="column-value"
									:style="`height: ${
										item.percentage || 0.1
									}%`"
								></div>
							</div>
							<p :class="{ last: index === 23 }">
								{{ item.label }}h
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Swal from 'sweetalert2'
	import _ from 'lodash'

	export default {
		name: 'DistributionGraphCard',
		props: {
			title: {
				type: String,
				required: true,
			},
			goal: {
				type: [Number, null],
				required: false,
				default: null,
			},
			value: {
				type: [Number, Object],
				required: true,
			},
			type: {
				type: String,
				required: true,
			},
			hasFilters: {
				type: Boolean,
				default: false,
			},
			reverse: {
				type: Boolean,
				default: false,
			},
			graphType: {
				type: String,
				default: 'progress',
			},
		},
		data() {
			return {
				progressInterval: null,
				animatedProgress: 0,
				reachedBackgroundColor: 'var(--pendingBackgroundColor)',
				reachedColor: 'var(--pendingColor)',
				reachedHoverBackgroundColor:
					'var(--pendingHoverBackgroundColor)',
				notReachedBackgroundColor: 'var(--dangerBackgroundColor)',
				notReachedColor: 'var(--dangerColor)',
				aboveBackgroundColor: 'var(--newPrimaryBackgroundColor)',
				aboveColor: 'var(--newPrimaryColor)',
				noGoalBackgroundColor: 'var(--blueBackgroundColor)',
				noGoalColor: 'var(--blueColor)',
			}
		},
		computed: {
			max() {
				if (this.goal === null) return 100
				if (this.goal === 0) return 100
				return this.goal + this.goal / 3
			},
			basicDistribution() {
				const distribution = Object.entries(this.value).map(
					([key, val]) => {
						return {
							label: key,
							value: val || 0,
							percentage:
								this.max === 0 ? 0 : (val / this.max) * 100,
						}
					}
				)

				return this.SortDistribution(distribution)
			},
			distributionData() {
				if (this.goal === null) return this.basicDistribution
				if (this.goal === 0) return this.basicDistribution

				const distribution = _.orderBy(
					Object.entries(this.value).map(([key, val]) => ({
						label: key,
						value: val,
						percentage: this.max === 0 ? 0 : (val / this.max) * 100,
					})),
					'label',
					'asc'
				)

				const sortedDistribution = this.SortDistribution(distribution)

				return sortedDistribution
			},
		},
		methods: {
			ChangeGoalQuestion(key) {
				Swal.fire({
					title: 'PREENCHA COM A NOVA META',
					customClass: {
						container: 'themed change-goal-modal',
					},
					input: 'text',
					inputValue:
						this.goal !== null && this.goal !== undefined
							? String(this.goal)
							: '',
					inputValidator: value => {
						if (value !== '' && isNaN(value)) {
							return 'PRECISA SER UM NÚMERO VÁLIDO!'
						}

						if (value < 0) {
							return 'PRECISA SER UM NÚMERO MAIOR OU IGUAL A 0!'
						}
					},
					showCancelButton: true,
					reverseButtons: true,
					cancelButtonText: 'CANCELAR',
					confirmButtonText: 'SALVAR',
				}).then(response => {
					if (response.isConfirmed) {
						this.$emit('change-goal', {
							key,
							value:
								response.value === '' ? null : +response.value,
						})
					}
				})
			},
			SortDistribution(distribution) {
				const nowHour = new Date().getHours()
				const nowIndex = distribution.findIndex(
					item => Number(item.label) === nowHour
				)
				const first_slice = distribution.slice(0, nowIndex + 1)
				const second_slice = distribution.slice(
					nowIndex + 1,
					distribution.length
				)

				const sortedDistribution = [...second_slice, ...first_slice]
				return sortedDistribution
			},
			GetIndicatorContainerWidth() {
				const baseWidth = 2.5
				const baseLength = 1

				if (this.goal === null) return `1.15rem`
				const actualLength = this.goal.toString().length
				if (baseLength >= actualLength) return `${baseWidth}rem`

				const newWidth = Math.abs(actualLength - baseLength) + baseWidth

				return `${newWidth}rem`
			},
		},
	}
</script>

<style lang="scss" scoped>
	.graph-card {
		padding: 1rem;
		background-color: var(--elementsBackgroundColor);
		border: 1px solid var(--elementsBackgroundColor);
		border-radius: 1rem;
		display: flex;
		flex-direction: column;
		align-items: center;

		.graph-header {
			width: 100%;
			display: flex;

			h3 {
				color: var(--cardfontColor);
				font-size: 1.25rem;
				line-height: 1.75rem;
				text-transform: uppercase;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
				margin-bottom: 0;
			}
		}

		.graph-main {
			width: 100%;
			flex-grow: 1;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;

			.graph-body {
				width: 100%;
				flex-grow: 1;
				display: flex;
				align-items: center;
				gap: 3rem;
			}

			.graph-footer {
				min-width: 30%;
				display: flex;
				justify-content: center;

				.graph-legend {
					.graph-legend-container {
						display: flex;
						justify-content: center;
						align-items: baseline;
						gap: 0.8rem;
						cursor: pointer;

						i {
							color: var(--reachedBackgroundColor);
							font-size: 1.5rem;
						}

						p {
							font-size: 1.5rem;
							margin-bottom: 0;
							color: var(--cardfontColor);
						}
					}
				}
			}
		}

		&.type-distribution {
			.graph-main {
				.graph-body {
					flex-direction: column;

					.distribution {
						--progress: 0;
						--color: var(--notReachedBackgroundColor);
						--fontSize: 7rem;

						width: 100%;
						flex-grow: 1;
						display: flex;
						align-items: stretch;
						position: relative;

						.indicator-container {
							--indicatorSize: 3rem;
							--containerWidth: 2.5rem;
							width: var(--containerWidth);
							height: calc(100% - 1.4rem);
							display: flex;
							flex-direction: column;
							position: relative;

							.indicator-wrapper {
								display: flex;
								justify-content: center;
								align-items: center;
								gap: 0.5rem;
								font-size: var(--indicatorSize);
								transform: translateY(-50%);
								position: absolute;
								top: calc(100% - 75%);
								right: 0;
								cursor: pointer;

								.goal-value {
									font-size: 1.5rem;
									font-weight: 900;
									color: var(--fontColor);
								}

								i {
									display: flex;
									justify-content: center;
									align-items: center;
									color: var(--reachedBackgroundColor);
								}

								&:hover {
									i {
										color: var(
											--reachedHoverBackgroundColor
										);
									}
								}
							}

							&.noGoal {
								.indicator-wrapper {
									i {
										opacity: 0.3;
									}
								}
							}
						}

						.distribution-container {
							height: 100%;
							flex-grow: 1;
							display: grid;
							grid-template-columns: repeat(24, 1fr);
							gap: 0.1rem;

							.distribution-item {
								height: 100%;
								width: 100%;
								display: flex;
								flex-direction: column;
								justify-content: center;

								p {
									height: 1.4rem;
									margin: 0;
									font-size: 0.675rem;
									opacity: 0.85;
									text-align: center;
									padding-top: 0.5rem;
								}

								.column-value-container {
									flex-grow: 1;
									display: flex;
									flex-direction: column;
									justify-content: flex-end;

									.column-value {
										background-color: var(--color);
									}
								}

								&.reached {
									--color: var(--reachedBackgroundColor);
								}

								&.above {
									--color: var(--aboveBackgroundColor);
								}

								&.noGoal {
									--color: var(--noGoalBackgroundColor);
								}

								&.reverse {
									--color: var(--aboveBackgroundColor);

									&.reached {
										--color: var(--reachedBackgroundColor);
									}

									&.above {
										--color: var(
											--notReachedBackgroundColor
										);
									}

									&.noGoal {
										--color: var(--noGoalBackgroundColor);
									}
								}
							}
						}
					}
				}
			}
		}
	}
</style>
